import { Fragment } from 'react'
import type { ReactNode } from 'react'
import type { ThemeTokens } from '../theme'

export interface DataTableColumn<T> {
  key: string
  header: string
  render: (row: T) => ReactNode
  align?: 'left' | 'right' | 'center'
  width?: string
}

export interface DataTableProps<T> {
  columns: Array<DataTableColumn<T>>
  rows: T[]
  getRowKey: (row: T) => string
  /** Sets `aria-label` on the `<tr>` so tests/assistive tech can target a
   *  row by e.g. email rather than relying on cell content order. */
  getRowLabel?: (row: T) => string
  tokens: ThemeTokens
  emptyLabel?: string
  /**
   * Makes the whole row activate, not just a button at the far right edge.
   *
   * A row that opens a drawer only from a 60px target at the end of eight
   * columns is a target you have to aim at; the row is the thing the eye is
   * already on. When supplied, the row gets `tabIndex`, a pointer cursor
   * and Enter/Space handling, so it is reachable by keyboard as well as
   * mouse.
   *
   * NOT `role="button"`, which the spec's sketch suggested: that overrides
   * the `<tr>`'s implicit `row` role, and a row that is not a row is no
   * longer announced as part of the table at all. The row keeps its
   * semantics and the visible affordance stays in its own column, which is
   * what a screen-reader user reaches for; `aria-haspopup` tells them what
   * activating the row itself will do.
   */
  onRowActivate?: (row: T) => void
  /**
   * Renders an expanded panel INSIDE the table, in a full-width row directly
   * beneath the row it belongs to.
   *
   * The alternative — a panel below the table — makes the reader scroll away
   * from the row they clicked and lose which person they are editing, which
   * is exactly what happened on the People page with 26 rows.
   *
   * Returning null (or omitting `isRowExpanded`) renders no extra row at all,
   * so tables that do not expand are byte-identical to before.
   */
  renderExpanded?: (row: T) => ReactNode
  isRowExpanded?: (row: T) => boolean
}

/**
 * Minimal, theme-driven table shared by every Task 8 page. Deliberately
 * plain (no virtualization, no client-side sort/paginate) — every list this
 * admin panel renders (users, budgets, keys, access report rows) is at most
 * a few hundred rows.
 */
export function DataTable<T>({
  columns,
  rows,
  getRowKey,
  getRowLabel,
  tokens,
  emptyLabel = 'Nothing to show yet.',
  onRowActivate,
  renderExpanded,
  isRowExpanded,
}: DataTableProps<T>) {
  if (rows.length === 0) {
    return <p style={{ color: tokens.textSecondary, fontSize: '0.86rem', margin: 0 }}>{emptyLabel}</p>
  }

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.86rem' }}>
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                style={{
                  textAlign: col.align ?? 'left',
                  padding: '0.5rem 0.65rem',
                  borderBottom: `1px solid ${tokens.border}`,
                  color: tokens.textSecondary,
                  fontWeight: 600,
                  fontSize: '0.74rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.02em',
                  width: col.width,
                  whiteSpace: 'nowrap',
                }}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <Fragment key={getRowKey(row)}>
            <tr
              aria-label={getRowLabel?.(row)}
              aria-haspopup={onRowActivate ? 'dialog' : undefined}
              tabIndex={onRowActivate ? 0 : undefined}
              style={onRowActivate ? { cursor: 'pointer' } : undefined}
              onClick={
                onRowActivate
                  ? (event) => {
                      // Never swallow a click that landed on a real control
                      // inside the row — the overflow menu and its items
                      // have to keep working.
                      const target = event.target as HTMLElement
                      if (target.closest('button, a, input, select, textarea')) {
                        return
                      }
                      onRowActivate(row)
                    }
                  : undefined
              }
              onKeyDown={
                onRowActivate
                  ? (event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        if (event.target !== event.currentTarget) {
                          return
                        }
                        event.preventDefault()
                        onRowActivate(row)
                      }
                    }
                  : undefined
              }
            >
              {columns.map((col) => (
                <td
                  key={col.key}
                  style={{
                    padding: '0.6rem 0.65rem',
                    borderBottom: `1px solid ${tokens.border}`,
                    color: tokens.textPrimary,
                    textAlign: col.align ?? 'left',
                    verticalAlign: 'middle',
                  }}
                >
                  {col.render(row)}
                </td>
              ))}
            </tr>
            {renderExpanded && isRowExpanded?.(row) ? (
              /* colSpan spans every column so the panel is not trapped in one
                 cell's width. aria-label ties it to the person for assistive
                 tech, which a bare panel below the table could not do. */
              <tr aria-label={getRowLabel ? `${getRowLabel(row)} details` : undefined}>
                <td
                  colSpan={columns.length}
                  style={{
                    padding: '0.9rem 0.65rem',
                    borderBottom: `1px solid ${tokens.border}`,
                    background: tokens.surface,
                  }}
                >
                  {renderExpanded(row)}
                </td>
              </tr>
            ) : null}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  )
}
